import { useCallback, useEffect, useMemo, useState } from 'react';

import useEditUserPreferences from 'lib/hooks/useEditUserPreferences';
import { useUserPreferences } from 'lib/hooks/queries/user/useUserPreferences';

import { DONT_SHOW_CONFIRM_KEY } from './constants';

export const useConfirmPreference = ({ confirmKey }) => {
  const [localConfirmPreference, setLocalConfirmPreference] = useState(null);
  const { editUserPreferences } = useEditUserPreferences(DONT_SHOW_CONFIRM_KEY);

  const { data: confirmPreferences } = useUserPreferences({
    params: { name: DONT_SHOW_CONFIRM_KEY },
    dependencies: [DONT_SHOW_CONFIRM_KEY],
    options: {
      enabled: !!confirmKey
    }
  });

  const storedConfirmPreferences =
    confirmPreferences?.preferences?.value?.[DONT_SHOW_CONFIRM_KEY] || {};

  const persistedPreference = useMemo(() => {
    if (!confirmKey) return false;
    return storedConfirmPreferences?.[confirmKey] ?? false;
  }, [confirmKey, storedConfirmPreferences]);

  useEffect(() => {
    if (localConfirmPreference === null) return;
    if (localConfirmPreference === persistedPreference) {
      setLocalConfirmPreference(null);
    }
  }, [localConfirmPreference, persistedPreference]);

  useEffect(() => {
    setLocalConfirmPreference(null);
  }, [confirmKey]);

  const effectiveConfirmPreference =
    localConfirmPreference !== null ? localConfirmPreference : persistedPreference;

  const shouldSkipConfirm = !!confirmKey && effectiveConfirmPreference;

  const handlePreferencePersist = useCallback(
    async (value) => {
      setLocalConfirmPreference(value);

      if (!confirmKey) return;

      const updatedPreferences = {
        ...storedConfirmPreferences,
        [confirmKey]: value
      };

      try {
        await editUserPreferences({
          [DONT_SHOW_CONFIRM_KEY]: updatedPreferences
        });
      } catch (error) {
        setLocalConfirmPreference(null);
      }
    },
    [confirmKey, editUserPreferences, storedConfirmPreferences]
  );

  return {
    shouldSkipConfirm,
    handlePreferencePersist
  };
};
